import {
  Autocomplete,
  LoadingOverlay,
  Modal,
  Stack,
  TextInput,
} from "@mantine/core"
import { useForm, zodResolver } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { CountryIso3166 } from "@tfg/db"
import { Button, IconNavion, CountrySelector } from "@tfg/ui"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { LibraryResponses } from "@tfg/types/endpoints/library"
import { transferContactSchema } from "@tfg/types/schemas/organization/schemas"

import {
  useTransferSupplierContactCertificatesMutation,
  useTransferSupplierContactMutation,
} from "~/pages/Brand/Suppliers/List/supplierHooks.ts"
import { useListOrganizationContactsQuery } from "~/pages/Brand/Suppliers/hooks.ts"
import { COUNTRIES } from "~/utils/countries.ts"

interface Props {
  opened: boolean
  closeModal: () => void
  contact: LibraryResponses.SupplierDetails
  type: "informationsRequests" | "certiticates"
}

export const TransferContactModal = ({
  opened,
  closeModal,
  contact,
  type,
}: Props) => {
  const { t } = useTranslation("suppliers")

  const form = useForm<z.infer<typeof transferContactSchema>>({
    validate: zodResolver(transferContactSchema),
    initialValues: {
      email: "",
      name: "",
      companyName: "",
      companyCountry: CountryIso3166.FR,
      language: contact.language,
      oldContactUserId: contact.id,
    },
  })
  const onClose = () => {
    form.reset()
    closeModal()
  }
  const { data: contacts, isLoading: isLoadingContacts } =
    useListOrganizationContactsQuery()

  const { mutate: transferInformationRequests, isLoading } =
    useTransferSupplierContactMutation({
      onSuccess: (count) => {
        notifications.show({
          message: t("supplier.transferModal.transferSuccess", {
            count: count.count,
          }),
          color: "green",
        })
        onClose()
      },
      onError: (error) => {
        notifications.show({
          message: error.message,
          color: "red",
        })
      },
    })

  const { mutate: transferCertificates, isLoading: isCertificatesLoading } =
    useTransferSupplierContactCertificatesMutation({
      onSuccess: (count) => {
        notifications.show({
          message: t("supplier.transferModal.transferSuccess", {
            count: count.count,
          }),
          color: "green",
        })
        onClose()
      },
      onError: (error) => {
        notifications.show({
          message: error.message,
          color: "red",
        })
      },
    })

  const transfer =
    type === "informationsRequests"
      ? transferInformationRequests
      : transferCertificates

  const handleSubmit = (form: z.infer<typeof transferContactSchema>) => {
    if (contact) {
      transfer({
        email: form.email,
        name: form.name,
        companyName: form.companyName,
        companyCountry: form.companyCountry,
        language: contact.language,
        oldContactUserId: contact.id,
      })
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("supplier.transferModal.title")}
      size="xl"
    >
      <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
        <Stack>
          <Autocomplete
            data={contacts?.map((c) => ({
              value: c.id.toString(),
              label: c.email,
            }))}
            autoFocus
            placeholder={t("supplier.transferModal.emailPlaceholder")}
            label={t("supplier.transferModal.email")}
            {...form.getInputProps("email")}
            onOptionSubmit={(value) => {
              const contact = contacts?.find((c) => c.id === value)
              if (contact) {
                form.setFieldValue("name", contact.name)
                form.setFieldValue("email", contact.email)
                form.setFieldValue("companyName", contact.companyName)
                form.setFieldValue("companyCountry", contact.companyCountry)
              } else {
                form.setFieldValue("email", value)
              }
            }}
          />
          <TextInput
            placeholder={t("supplier.transferModal.namePlaceholder")}
            label={t("supplier.transferModal.name")}
            {...form.getInputProps("name")}
          />
          <TextInput
            placeholder={t("supplier.transferModal.companyNamePlaceholder")}
            label={t("supplier.transferModal.companyName")}
            {...form.getInputProps("companyName")}
          />
          <CountrySelector
            placeholder={t("supplier.transferModal.companyCountryPlaceholder")}
            label={t("supplier.transferModal.companyCountry")}
            countriesMapper={COUNTRIES}
            onChange={form.getInputProps("companyCountry").onChange}
            value={form.getInputProps("companyCountry").value}
          />
          <Button
            type="submit"
            variant="primary"
            icon={<IconNavion hovered={false} />}
            loading={isLoading || isCertificatesLoading}
          >
            {t("supplier.transferModal.transfer")}
          </Button>
        </Stack>

        <LoadingOverlay
          visible={isLoading || isLoadingContacts}
          overlayProps={{ blur: 2 }}
        />
      </form>
    </Modal>
  )
}
